import React, {
  useEffect, useState, useRef, Suspense, useMemo, useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import { orderBy, uniqBy } from 'lodash';
import Seo from '../../Seo/Seo';
import PlainText from '../../UI/PlainText';
import useLocation from '../../../utils/location';
import TextLink from '../../UI/TextLink/TextLink';
import ProductGallery from '../../elements/ProductGallery/ProductGallery';
import VariantDetails from '../../AccessoriesComponents/VariantDetails/VariantDetails';
import { isKiosk } from '../../../utils/isKiosk';
import extractArrayData from '../../../utils/extractArrayData';
import { Contentful } from '../../../types';
import BackToPageLink from '../../UI/BackToPageLink/BackToPageLink';
import { Variant as VariantI } from '../../AccessoriesComponents/VariantDetails/types';
import { ContentfulAccessory, EColour } from '../../../ContentfulTypes';
import {
  Section,
  Container,
  Variant,
  Gallery,
  ImageGalleryContainer,
  CtaContainer,
  Headline,
  Manufacturer,
  ProductSku,
  ProductSkuItem,
  ProductSkuBrand,
} from './styles';

const LegalText = React.lazy(() => import('../../UI/LegalText/LegalText'));

const SpecSection = React.lazy(() => import('../../AccessoriesComponents/SpecSection/SpecSection'));

interface Props {
  contentfulData: ContentfulAccessory;
  productDetailsPageData: Record<string, any>;
}

const AccessoryDetailsScreen = ({
  contentfulData,
  productDetailsPageData,
}: Props) => {
  const { locale } = useIntl();
  const [state, setState] = useState<{
    selectedColour?: string;
    selectedVariant?: VariantI;
    variantList: VariantI[];
    colourList: EColour[];
  }>({
    variantList: contentfulData?.variants || [],
    colourList: [],
  });

  const colourList = useMemo(() => uniqBy(
    orderBy(contentfulData?.variants, ['price'], ['asc'])
      .filter(({ accessoryColour }) => accessoryColour)
      .map((item) => ({
        ...item.accessoryColour,
        hexValue: item.accessoryColour?.hexValue?.replace(/[^A-z0-9]/g, ''),
      })),
    (item) => item.displayName,
  ), [contentfulData?.variants]);

  const variantList = useMemo(() => {
    const variants = contentfulData?.variants || [];
    if (state.selectedColour) {
      return orderBy(
        variants.filter(
          ({ accessoryColour }) => accessoryColour?.displayName === state.selectedColour,
        ),
        ['price'],
        ['asc'],
      );
    }
    return orderBy(variants, ['price'], ['asc']);
  }, [contentfulData?.variants, state.selectedColour]);

  // Order the variants colours in ascending price of variants
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      colourList,
    }));
  }, [colourList]);
  
  // This useEffect will create a list of variants as per ascending price
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      variantList,
      selectedVariant: variantList[0],
      selectedColour: variantList[0]?.accessoryColour.displayName,
    }));
  }, [variantList]);
  
  // Scroll to top of page on load
  useEffect(() => {
    const scrollToTop = () => {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    };
  
    const timeout = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timeout);
  }, []);

  // Set smooth scroll to product details section
  const specsRef = useRef<HTMLElement>();
  const { homePage, currentPage } = useLocation();

  const specsScroll = useCallback(() => {
    setTimeout(() => {
      if (specsRef.current) specsRef.current.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  }, []);
  
  const seoTitle = useMemo(() => {
    const isEnglish = locale === 'en';
    const title = isEnglish ? 'Accessories' : 'Accessoires';
    const displayNameTitle = contentfulData.displayName ? `- ${contentfulData.displayName}` : '';
    return `${title} ${
      displayNameTitle
    }  | ${isEnglish ? 'The Mobile Shop' : 'La Boutique Mobile'}`;
  }, [contentfulData.displayName, locale]);

  // LEGAL DETAILS DATA ----------------------------------------------
  // Legal text for page - needs to be delivered in array
  // PCO points legal
  const { contentBody: legalTextSectionPco, heading: legalTextHeadingPco } = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '7KuU6QrE2xznhvHPfUaBUE',
  );

  // Kiosk legal
  const { contentBody: legalTextSectionKiosk, heading: legalTextHeadingKiosk } = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '54L9TG8mWgnY2vP58uzhj8',
  );

  // Default legal
  const {
    contentBody: legalTextSectionDefault,
    heading: legalTextHeadingDefault,
  } = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '2eRqxCLUZeeUIr8q7L1DyL',
  );

  const isKioskMode = isKiosk();

  const [legalText, legalHeading] = useMemo(() => {
    let text = [legalTextSectionDefault];
    let heading = legalTextHeadingDefault;
    if (state.selectedVariant?.pcoPoints) {
      text = [legalTextSectionPco];
      heading = legalTextHeadingPco;
    } else if (isKioskMode) {
      text = [legalTextSectionKiosk];
      heading = legalTextHeadingKiosk;
    }
    return [text, heading];
  }, [
    isKioskMode,
    legalTextHeadingDefault,
    legalTextHeadingKiosk,
    legalTextHeadingPco,
    legalTextSectionDefault,
    legalTextSectionKiosk,
    legalTextSectionPco,
    state.selectedVariant?.pcoPoints,
  ]);
  
  if (!contentfulData?.variants?.length) return null;

  // Spec label data -----------------------------------------
  const specLabels = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '4TbQ5bw8avQyXoz3EJE35b',
  )?.formFields;

  const navigationFormFields = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '13flvku8mC81Yydbfw6sxv',
  ).formFields;

  const navigationFormLabels = navigationFormFields.reduce((obj, item) => {
    obj[item.contentful_id] = item.displayLabel;
    return obj;
  }, {});

  // Variant label data -----------------------------------------
  const variantLabels = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '7r2gjzQLVxRnvUPOC6nzUk',
  )?.formFields;

  // Spec label data -----------------------------------------
  const specScrollLabel = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '2haPvjm0Bi4OxAOqh07cFN',
  )?.combinatorSectionItems;

  // Inventory Labels -----------------------------------------
  const inventoryLabels = extractArrayData(
    productDetailsPageData.pageSections,
    Contentful.ContentfulId,
    '1JSPIARWgmybx9qAdTLLRp',
  );

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: homePage,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: PlainText({
          document: contentfulData.displayName,
          options: {},
        }),
        item: currentPage,
      },
    ],
  };

  return (
    <>
      <Seo
        title={seoTitle}
        description={contentfulData.displayName}
        ldJson={schema}
      />
      {contentfulData.displayName && (
        <>
          <Section>
            <Container>
              <Gallery>
                {/* BACK TO ACCESSORIES */}
                {/* TODO Update Id */}
                <BackToPageLink
                  label={navigationFormLabels['32Qvk3aFQjRhdgeEMNMiGN']}
                  to={`/${locale}/accessories`}
                />
                {/** Manufacturer name on mobile screen */}
                {contentfulData.manufacturerName && (
                  <Manufacturer>
                    {contentfulData.manufacturerName}
                  </Manufacturer>
                )}

                {/** Headline on mobile screen */}
                <Headline>
                  {contentfulData?.displayName}
                </Headline>
                {(contentfulData.manufacturerName && state.selectedVariant) && (
                  <ProductSku>
                    {contentfulData.manufacturerName && (
                      <ProductSkuItem>
                        <strong>{variantLabels?.[7].displayLabel}</strong>
                        <ProductSkuBrand
                          to={`/${locale}/accessories/?brand=${contentfulData.manufacturerName.replace(/\s+/g, '-')}`}
                          aria-label={contentfulData.manufacturerName}
                        >
                          {contentfulData.manufacturerName}
                        </ProductSkuBrand>
                      </ProductSkuItem>
                    )}
                    {state.selectedVariant?.manufacturerModel && (
                      <ProductSkuItem>
                        <strong>{variantLabels?.[8].displayLabel}</strong>
                        {state.selectedVariant?.manufacturerModel}
                      </ProductSkuItem>
                    )}
                    {state.selectedVariant?.productSku && (
                      <ProductSkuItem>
                        <strong>{variantLabels?.[9].displayLabel}</strong>
                        {state.selectedVariant?.productSku?.includes('-') ? variantLabels?.[10].displayLabel : state.selectedVariant?.productSku}
                      </ProductSkuItem>
                    )}
                  </ProductSku>
                )}
                <ImageGalleryContainer>
                  <ProductGallery
                    variant={state.selectedVariant || contentfulData?.variants[0]}
                  />
                </ImageGalleryContainer>
                {contentfulData?.specifications && (
                  <CtaContainer>
                    <TextLink
                      linkText={specScrollLabel[0]?.ctaLabel.ctaLabel}
                      locale={locale}
                      onClick={specsScroll}
                      data-testid="specs-text-link"
                    />
                  </CtaContainer>
                )}
              </Gallery>
              {/** Variant Section */}
              <Variant>
                <VariantDetails
                  contentfulData={contentfulData}
                  variantLabels={variantLabels}
                  colourList={state.colourList}
                  selectedColour={state.selectedColour}
                  setSelectedColour={
                    (selected) => setState((prev) => ({ ...prev, selectedColour: selected }))
                  }
                  variantList={state.variantList}
                  selectedVariant={state.selectedVariant}
                  setSelectedVariant={
                    (selected) => setState((prev) => ({ ...prev, selectedVariant: selected }))
                  }
                  inventoryLabels={inventoryLabels}
                />
              </Variant>
            </Container>
          </Section>

          {/** Spec Section */}
          <Suspense>
            <SpecSection
              ref={specsRef}
              specLabels={specLabels}
              specifications={contentfulData?.specifications || []}
              headingBody={contentfulData?.description?.description}
            />
          </Suspense>

          {/* Legal Details - Legal Text */}
          <Suspense>
            <LegalText
              preExpand
              legalText={legalText}
              legalHeading={legalHeading}
              pageId="connect"
            />
          </Suspense>
        </>
      )}
    </>
  );
};

export default AccessoryDetailsScreen;
